import { Collapse, Divider, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ActuatorOption from '../base/ActuatorOption'
import { FormProvider, useForm } from 'react-hook-form'
import { ActuatorPayload, BatteryActuator, BatteryActuatorForm } from '../../models/smart-home.model'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import MultilineActorBase from '../base/MultilineActorBase'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { LoadingButton } from '@mui/lab'
import { useActuatorUpdate } from '../../hooks/use-actuator-update'
import { useBatteryActuatorFormInitializer } from '../../hooks/actuator-form-initializer/use-battery-actuator-form-initializer'
import { useAutoActuatorHeaderContent } from '../../hooks/use-auto-actuator-header-content'
import ActuatorAutoOption from '../base/ActuatorAutoOption'
import ActuatorRepeatOption from '../base/ActuatorRepeatOption'

interface Props {
  serialNo: number
  actuator: BatteryActuator
}

const INPUT_WIDTH = 230

const Battery: React.FC<Props> = ({ serialNo, actuator }) => {
  const { t } = useCustomTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedState, setSelectedState] = useState(actuator.state === 'on')
  const [showAutoOptions, setShowAutoOptions] = useState(actuator.descriptions.auto ?? false)
  const { initialValues, validationSchema } = useBatteryActuatorFormInitializer(actuator)
  const headerContent = useAutoActuatorHeaderContent({
    serialNo,
    actuator,
    showAutoOptions,
    isLoading,
    selectedState,
    setIsLoading,
    setSelectedState,
  })
  const onUpdateActuator = useActuatorUpdate({ setIsLoading })

  const methods = useForm<BatteryActuatorForm>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })

  const autoValue = methods.watch('auto')

  useEffect(() => {
    if (autoValue || (!autoValue && actuator.descriptions.auto)) {
      setShowAutoOptions(true)
    } else {
      setShowAutoOptions(false)
    }
  }, [autoValue, actuator.descriptions.auto])

  const onSubmit = (values: BatteryActuatorForm): void => {
    const payload: ActuatorPayload = { command: values.auto ? 'auto' : 'manual', descriptions: { ...values } }
    onUpdateActuator(serialNo, actuator, payload)
  }

  return (
    <FormProvider {...methods}>
      <MultilineActorBase actuator={actuator} headerContent={headerContent}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack
            sx={{ backgroundColor: 'background.paper2', borderColor: 'grey.grey06', py: 2, px: 4, mt: 2 }}
            border={1}
            gap={2}
            borderRadius={theme => `${theme.shape.borderRadius}px`}
          >
            <ActuatorAutoOption />
            <Collapse in={showAutoOptions} unmountOnExit>
              <Stack gap={2}>
                <ActuatorOption title={'smart-home.battery.soc-on.title'}>
                  <TextField name="socOn" clearable={false} fullWidth={false} suffix="%" sx={{ mb: 2, maxWidth: { xs: 'auto', sm: INPUT_WIDTH } }} />
                </ActuatorOption>
                <ActuatorOption title={'smart-home.battery.soc-off.title'}>
                  <TextField name="socOff" clearable={false} fullWidth={false} suffix="%" sx={{ mb: 2, maxWidth: { xs: 'auto', sm: INPUT_WIDTH } }} />
                </ActuatorOption>
                <ActuatorOption title={'smart-home.battery.min-on-time.title'} description={'smart-home.battery.min-on-time.description'}>
                  <TextField
                    name="minOn"
                    fullWidth={false}
                    clearable={false}
                    mask={'##:##'}
                    sx={{ mb: 2, maxWidth: { xs: 'auto', sm: INPUT_WIDTH } }}
                    suffix={t('affixes.hours')}
                  />
                </ActuatorOption>
                <ActuatorOption title={'smart-home.battery.min-off-time.title'} description={'smart-home.battery.min-off-time.description'}>
                  <TextField
                    name="minOff"
                    fullWidth={false}
                    clearable={false}
                    mask={'##:##'}
                    sx={{ mb: 2, maxWidth: { xs: 'auto', sm: INPUT_WIDTH } }}
                    suffix={t('affixes.hours')}
                  />
                </ActuatorOption>
                <ActuatorRepeatOption />
                <Divider sx={{ mx: -4, borderColor: 'grey.grey06' }} />
                <Stack direction="row" justifyContent="flex-end" gap={2}>
                  <LoadingButton type="submit" variant="contained" disabled={!methods.formState.isDirty} loading={isLoading}>
                    {t('common.save')}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        </form>
      </MultilineActorBase>
    </FormProvider>
  )
}

export default Battery
